import type { TypeTableColumn } from '@thefarmersfront/kitchen';

import type { CellContext } from '@tanstack/react-table';

import { SettlementStorageDetailCustomHeaderChildren as CustomHeaderGroupChildren } from '~/components/settlement';

import { formatNumber } from '~/utils';

import type { TypeGetStorageFeeDailySkusResponse } from '~/types/settlement.types';

type TypeStorageFeeDailySku = TypeGetStorageFeeDailySkusResponse['data'][number];

const numberCell = ({ getValue }: CellContext<TypeStorageFeeDailySku, unknown>) => {
  const value = getValue<number | null | undefined>();
  return value !== null && value !== undefined ? formatNumber(value) : '-';
};

const NUMBER_CELL_STYLE = { cellStyle: { textAlign: 'right' as const } };

const rangeColumn = (
  accessorKey: `storageFeePerRange.${keyof TypeStorageFeeDailySku['storageFeePerRange']}`,
  label: string
): TypeTableColumn<TypeStorageFeeDailySku> => ({
  accessorKey,
  header: () => <CustomHeaderGroupChildren displayName={label} />,
  cell: numberCell,
  size: 100,
  meta: NUMBER_CELL_STYLE,
});

/**
 * [정산관리][보관비 조회] 일자 별 보관비 상세 모달 컬럼 정의
 *
 * 컬럼마다 그룹 헤더를 두어 2단 헤더로 그린다. 하위 칸이 하나뿐인 그룹은 하위 헤더를 비운다.
 */
const getSettlementStorageDailyFeeDetailColumnDefs = () => {
  const columnDefs: TypeTableColumn<TypeStorageFeeDailySku>[] = [
    {
      id: 'noGroup',
      header: 'No',
      columns: [{ id: 'no', header: '', cell: ({ row }) => row.index + 1, size: 72 }],
    },
    {
      id: 'skuNameGroup',
      header: '상품명',
      columns: [{ accessorKey: 'skuName', header: '', size: 240 }],
    },
    {
      id: 'skuCodeGroup',
      header: 'SKU코드',
      columns: [{ accessorKey: 'skuCode', header: '', size: 140 }],
    },
    {
      id: 'skuBarcodeGroup',
      header: '상품바코드',
      columns: [{ accessorKey: 'skuBarcode', header: '', size: 140 }],
    },
    {
      id: 'totalStorageFeeGroup',
      header: '총 보관비',
      columns: [{ accessorKey: 'totalStorageFee', header: '', cell: numberCell, size: 100, meta: NUMBER_CELL_STYLE }],
    },
    {
      id: 'chargedStorageFeeGroup',
      header: '실 과금액',
      columns: [{ accessorKey: 'chargedStorageFee', header: '', cell: numberCell, size: 100, meta: NUMBER_CELL_STYLE }],
    },
    {
      id: 'storageFeePerRangeGroup',
      header: '구간별 과금액(원)',
      columns: [
        rangeColumn('storageFeePerRange.RANGE_001', '30일 이하'),
        rangeColumn('storageFeePerRange.RANGE_002', '31~60일'),
        rangeColumn('storageFeePerRange.RANGE_003', '61~90일'),
        rangeColumn('storageFeePerRange.RANGE_004', '91일 이상'),
      ],
    },
    {
      id: 'quantityGroup',
      header: '시점 재고',
      meta: { headerTooltip: { description: '조회 날짜 기준\n보관비가 부과된 재고입니다.', maxWidth: '158px' } },
      columns: [{ accessorKey: 'quantity', header: '', cell: numberCell, size: 100, meta: NUMBER_CELL_STYLE }],
    },
    {
      id: 'cbmGroup',
      header: 'CBM(㎥)',
      columns: [{ accessorKey: 'cbm', header: '', cell: numberCell, size: 112, meta: NUMBER_CELL_STYLE }],
    },
  ];

  return { columnDefs };
};

export default getSettlementStorageDailyFeeDetailColumnDefs;
